import type { BackgroundSelection } from '@dcc/rules/rules/backgrounds.ts';
import { backgroundLabel, skillsAlreadyTaken } from '@dcc/rules/rules/backgrounds.ts';
import type { BackgroundTable } from '@dcc/rules/content/types.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';

/**
 * One background table: pick a background, then exactly the required number of
 * its Skills. Skills already taken from another table are shown disabled,
 * because Ranks from different backgrounds are not added together (DCC-BG-003).
 * Ported from the desktop creator.
 */
export function BackgroundStep(props: {
  table: BackgroundTable;
  selections: BackgroundSelection[];
  onChange: (selection: BackgroundSelection) => void;
}) {
  const table = props.table;
  const selection = props.selections.find((s) => s.tableId === table.id);
  const backgrounds = contentRegistry.backgroundsForTable(table.id);
  const chosen = selection ? contentRegistry.getBackground(selection.backgroundId) : undefined;
  const taken = skillsAlreadyTaken(props.selections, table.id);

  function pickBackground(backgroundId: string) {
    props.onChange({ tableId: table.id, backgroundId, skillIds: [] });
  }

  function toggleSkill(skillId: string) {
    if (!selection) return;
    const current = selection.skillIds;
    const next = current.includes(skillId) ? current.filter((id) => id !== skillId) : [...current, skillId];
    props.onChange({ ...selection, skillIds: next });
  }

  return (
    <section className="dcc-creator-card">
      <h2>Table {table.tableNumber}: {table.label}</h2>
      <p className="dcc-creator-help">
        Choose a background, then pick {table.chooseCount} of its Skills at Rank {table.startingRank}.
        (C&amp;C p.{table.source.page}; roll {table.die} if you prefer.)
      </p>
      <select
        className="dcc-creator-select"
        value={selection?.backgroundId ?? ''}
        onChange={(e) => pickBackground(e.currentTarget.value)}
      >
        <option value="">Choose a background</option>
        {backgrounds.map((b) => (
          <option key={b.id} value={b.id}>{backgroundLabel(b)}</option>
        ))}
      </select>

      {chosen && selection && (
        <div className="dcc-creator-options">
          {chosen.skillIds.map((skillId) => {
            const skill = contentRegistry.getSkill(skillId);
            const isTaken = taken.has(skillId);
            const isPicked = selection.skillIds.includes(skillId);
            const atLimit = selection.skillIds.length >= table.chooseCount && !isPicked;
            return (
              <button
                key={skillId}
                className={isPicked ? 'dcc-creator-option is-selected' : 'dcc-creator-option'}
                disabled={isTaken || atLimit}
                onClick={() => toggleSkill(skillId)}
                title={isTaken ? 'Already taken from another background' : undefined}
              >
                <span>{skill ? skill.name : skillId}</span>
                <span className="dcc-creator-meta">{isTaken ? 'already taken' : `Rank ${table.startingRank}`}</span>
              </button>
            );
          })}
          <p className="dcc-creator-help">{selection.skillIds.length} of {table.chooseCount} chosen.</p>
        </div>
      )}
    </section>
  );
}
